// New file: NavGroup.tsx (Reusable Component)
import React from "react";
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useLocation } from "react-router-dom";
import NavbarStyles from "./NavbarStyles";

interface NavItem {
  key: string;
  name: string;
}

interface NavGroupProps {
  open: boolean;
  groupLabel: string;
  groupIcon: React.ReactNode;
  items: NavItem[];
  expanded: boolean;
  onToggleExpand: (label: string) => void;
  onNavigate: (key: string) => void;
  getNavItemName: (key: string) => string;
  getIcon: (key: string) => React.ReactNode;
}

const NavGroup: React.FC<NavGroupProps> = ({
  open,
  groupLabel,
  groupIcon,
  items,
  expanded,
  onToggleExpand,
  onNavigate,
  getNavItemName,
  getIcon,
}) => {
  const theme = useTheme();
  const location = useLocation();

  const isGroupActive = items.some(
    ({ key }) => location.pathname === `/admin/${key.toLowerCase()}`
  );

  return (
    <>
      {/* Group Header */}
      {open ? (
        <ListItem disablePadding sx={NavbarStyles.groupHeaderContainer}>
          <Box sx={NavbarStyles.groupHeaderBox}>
            {groupIcon}
            <Typography variant="body2" sx={NavbarStyles.groupHeaderTypography}>
              {groupLabel}
            </Typography>
          </Box>
          {items.length > 0 && (
            <IconButton
              size="small"
              onClick={() => onToggleExpand(groupLabel)}
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </IconButton>
          )}
        </ListItem>
      ) : (
        <Tooltip title={groupLabel} placement="right-end">
          <ListItem disablePadding sx={NavbarStyles.collapsedGroupContainer}>
            <ListItemButton
              onClick={() => onToggleExpand(groupLabel)}
              sx={NavbarStyles.collapsedGroupButton(
                theme,
                expanded,
                isGroupActive
              )}
            >
              <ListItemIcon
                sx={NavbarStyles.collapsedGroupIcon(
                  theme,
                  expanded,
                  isGroupActive
                )}
              >
                {groupIcon}
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </Tooltip>
      )}

      {/* Group Items */}
      {expanded &&
        items.map(({ key }) => {
          const isActive = location.pathname === `/admin/${key.toLowerCase()}`;
          return (
            <Tooltip
              key={key}
              title={getNavItemName(key)}
              placement="right-end"
              disableHoverListener={open}
            >
              <ListItem disablePadding sx={NavbarStyles.groupItemContainer}>
                <ListItemButton
                  onClick={() => onNavigate(key)}
                  sx={NavbarStyles.groupItemButton(theme, open, isActive)}
                  aria-current={isActive ? "page" : undefined}
                >
                  <ListItemIcon
                    sx={NavbarStyles.groupItemIcon(theme, isActive)}
                  >
                    {getIcon(key)}
                  </ListItemIcon>
                  <ListItemText
                    primary={getNavItemName(key)}
                    sx={NavbarStyles.groupItemText(open, theme, isActive)}
                  />
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
    </>
  );
};

export default NavGroup;
